import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { hasExistingData, loadData, clearData } from '../utils/storage';
import { calendars } from '../api/index';

const FIRST_LOGIN_HANDLED_KEY = 'lincal-first-login-handled';

/**
 * Hook to manage first-login data merge flow
 * Shows merge modal when authenticated user has existing local data
 */
// Helper function to mark user as handled (not a hook, so it can be called anywhere)
function markUserAsHandled(userId) {
  const handledUsers = JSON.parse(localStorage.getItem(FIRST_LOGIN_HANDLED_KEY) || '[]');
  if (!handledUsers.includes(userId)) {
    handledUsers.push(userId);
    localStorage.setItem(FIRST_LOGIN_HANDLED_KEY, JSON.stringify(handledUsers));
  }
}

export function useFirstLoginMerge() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [localData, setLocalData] = useState(null);
  const [defaultCalendarId, setDefaultCalendarId] = useState(null);

  // Check for local data when user logs in
  useEffect(() => {
    if (authLoading) return;
    
    let mounted = true;
    
    async function checkFirstLogin() {
      if (!isAuthenticated || !user) return;
      
      // Check if we've already handled first login for this user
      const handledUsers = JSON.parse(localStorage.getItem(FIRST_LOGIN_HANDLED_KEY) || '[]');
      const alreadyHandled = handledUsers.includes(user.id);
      
      if (alreadyHandled || !hasExistingData()) return;
      
      const data = loadData();
      // Check if there's actually meaningful data (not just empty defaults)
      const hasProjects = data.projects?.filter(p => !p.isSystem)?.length > 0;
      const hasEvents = data.events?.length > 0;
      const hasMilestones = data.milestones?.length > 0;
      
      if (!hasProjects && !hasEvents && !hasMilestones) {
        // No meaningful data, just mark as handled
        markUserAsHandled(user.id);
        return;
      }
      
      try {
        // Fetch default calendar ID
        const calList = await calendars.list();
        const defaultCal = calList.owned?.find(c => c.isDefault) || calList.owned?.[0];
        
        if (mounted && defaultCal) {
          // Check if server has any non-system projects
          const calendarData = await calendars.get(defaultCal.id);
          const hasNonSystemProject = calendarData.projects?.some(p => !p.isSystem);
          
          if (!hasNonSystemProject) {
            // Server is empty - auto-merge without showing modal
            await calendars.import(defaultCal.id, data);
            clearData();
            markUserAsHandled(user.id);
            window.location.reload();
          } else {
            // Server has data - show modal with both options
            setLocalData(data);
            setDefaultCalendarId(defaultCal.id);
            setShowMergeModal(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch calendars:', err);
      }
    }
    
    checkFirstLogin();
    
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, authLoading, user]);

  const handleMerge = useCallback(async () => {
    if (!localData || !defaultCalendarId || !user) return;
    
    // Import local data to the default calendar
    await calendars.import(defaultCalendarId, localData);
    
    // Clear local data and mark as handled
    clearData();
    markUserAsHandled(user.id);
    
    // Reload to show merged data
    window.location.reload();
  }, [localData, defaultCalendarId, user]);

  const handleDiscard = useCallback(async () => {
    if (!user) return;
    
    // Clear local data
    clearData();
    markUserAsHandled(user.id);
    
    // Reload to show cloud data
    window.location.reload();
  }, [user]);

  const handleClose = useCallback(() => {
    setShowMergeModal(false);
  }, []);

  return {
    showMergeModal,
    localData,
    handleMerge,
    handleDiscard,
    handleClose,
  };
}

export default useFirstLoginMerge;
